import PropTypes from 'prop-types';
import React, {
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import * as publicStudioApi from '../../api/public/studio';
import * as studioApi from '../../api/studio/studio';
import { useAuthentication } from '../Authentication/Authentication';
import { usePublicSocket } from '../PublicSocket/Provider';
import { StudioStatus } from '../StudioSchedule/helper';
import { StudioContext, StudioModal, useStudio } from './Context';
import { StudioModalTerminate } from './ModalTerminate';

export { StudioContext, useStudio };

export const StudioProvider = ({ children }) => {
	const { isLoggedIn } = useAuthentication();
	const [accessStatus, setAccessStatus] = useState();
	const [controlroomActive, setControlroomActive] = useState(false);
	const [studio, setStudio] = useState();
	const [modalOpen, setModalOpen] = useState();
	const [openSurveyModalOnStop, setOpenSurveyModalOnStop] = useState(false);
	const [estimation, setEstimation] = useState(0);
	const [isEstimationBellowLimit, setIsEstimationBellowLimit] = useState(false);

	const { addLiveNotificationHandler } = usePublicSocket();

	const closeModal = useCallback(() => setModalOpen(undefined), []);

	const resetStudioState = useCallback(() => {
		setAccessStatus(undefined);
		setStudio(undefined);
		setModalOpen(undefined);
		setOpenSurveyModalOnStop(false);
	}, []);

	const requestStudioByCode = useCallback(async (code, requiredAccess = 'PUBLIC', forceJoin = undefined) => {
		try {
			const doRequest = isLoggedIn
				? () => studioApi.requestStudioByCode(code, requiredAccess, forceJoin)
				: () => publicStudioApi.requestPublicStudioByCode(code, requiredAccess, forceJoin);
			const { data } = await doRequest();
			const { accessStatus: a, ...s } = data;
			setAccessStatus(a);
			setStudio(s);
			return data;
		} catch (error) {
			setAccessStatus(undefined);
			setStudio(undefined);
			throw error;
		}
	}, [isLoggedIn]);

	const fetchPublicStudioByCode = useCallback(async (code) => {
		try {
			const { data } = await publicStudioApi.fetchOneStudioByCode(code);
			setStudio(data);
			return data;
		} catch (error) {
			setStudio(undefined);
			throw error;
		}
	}, []);

	const refetchStudio = useCallback(async () => {
		try {
			if (!studio?.code) throw new Error('Unknown studio to refetch');
			const { data } = await studioApi.fetchStudioByCode(studio.code);
			setStudio(data);
			return data;
		} catch (error) {
			setStudio(undefined);
			throw error;
		}
	}, [studio]);

	const updateStudio = useCallback((data) => {
		if (!studio || data._id !== studio?._id) return;
		setStudio({ ...studio, ...data });
	}, [studio]);

	const stopActiveStudio = useCallback(async () => {
		if (!studio) throw new Error('No active studio');
		const { data } = await studioApi.stopStudio(studio._id);
		setStudio(data);
		return data;
	}, [studio]);

	const golive = useCallback(async (startTime) => {
		if (!studio) throw new Error('No active studio');
		const { data } = await studioApi.golive(studio._id, startTime);
		setStudio(data);
		return data;
	}, [studio]);

	const stoplive = useCallback(async () => {
		if (!studio) throw new Error('No active studio');
		const { data } = await studioApi.stoplive(studio._id);
		setStudio(data);
		return data;
	}, [studio]);

	const handleEventStudioStatus = useCallback(async (studioUpdate) => {
		setStudio((state) => {
			if (!state || state._id !== studioUpdate._id) return state;
			return {
				...state,
				...studioUpdate,
			};
		});
	}, []);

	useEffect(() => {
		if (!studio) return undefined;
		const removeLiveNotificationHandler = addLiveNotificationHandler((studioUpdate) => {
			handleEventStudioStatus(studioUpdate);
		});
		return () => {
			removeLiveNotificationHandler();
		};
	}, [studio, addLiveNotificationHandler, handleEventStudioStatus]);

	const handleStudioCharge = useCallback((notification) => {
		if (notification.studio === studio?._id) {
			const minutesLeft = Math.ceil(
				(notification.balance + notification.chipBalance) / notification.lastMinuteCharge,
			);
			const totalEstimation =	minutesLeft
				+ (studio ? Math.floor((new Date() - new Date(studio?.readyAt)) / 60000) : 0);

			setEstimation(Math.min(12 * 60, totalEstimation));
			setIsEstimationBellowLimit(minutesLeft <= 5);
		}
	}, [studio]);

	const channel = studio?.owner;
	const isStudioStarted = [
		StudioStatus.RUNNING,
		StudioStatus.WAITING_FOR_CONTROLLER,
		StudioStatus.PENDING,
	].includes(studio?.status);

	const context = useMemo(() => ({
		accessStatus,
		channel,
		controlroomActive,
		resetStudioState,
		closeModal,
		fetchPublicStudioByCode,
		golive,
		modalOpen,
		refetchStudio,
		requestStudioByCode,
		setAccessStatus,
		setControlroomActive,
		setModalOpen,
		setStudio,
		handleEventStudioStatus,
		stopActiveStudio,
		stoplive,
		studio,
		isStudioStarted,
		estimation,
		isEstimationBellowLimit,
		handleStudioCharge,
		updateStudio,
		openSurveyModalOnStop,
		setOpenSurveyModalOnStop,
	}), [
		accessStatus,
		channel,
		controlroomActive,
		resetStudioState,
		closeModal,
		fetchPublicStudioByCode,
		golive,
		modalOpen,
		refetchStudio,
		requestStudioByCode,
		handleEventStudioStatus,
		stopActiveStudio,
		stoplive,
		studio,
		isStudioStarted,
		estimation,
		isEstimationBellowLimit,
		handleStudioCharge,
		updateStudio,
		openSurveyModalOnStop,
		setOpenSurveyModalOnStop,
	]);

	return (
		<StudioContext.Provider value={context}>
			{children}
			<Suspense fallback={null}>
				{modalOpen === StudioModal.TERMINATE && (
					<StudioModalTerminate
						isOpen
						stopActiveStudio={stopActiveStudio}
						toggle={closeModal}
					/>
				)}
			</Suspense>
		</StudioContext.Provider>
	);
};

StudioProvider.propTypes = {
	children: PropTypes.node,
};

StudioProvider.defaultProps = {
	children: undefined,
};
